import { Flex } from 'antd';
import InteractiveCard from '../../common/interactiveCard';
import { Divider, IconButton, Tooltip } from '../../Ant';
import { ArrowRight, Briefcase, CircleTick, Danger, EditLinear } from '../../icons';
import CardData from '../cardData';
import { useTranslation } from 'react-i18next';
import { ITask } from '../../../interface/tasks';
import { useLanguage } from '../../../context/LanguageContext';
import { useTask } from '../../../context/TaskContext';
import { ROUTES } from '../../../constants/constants';
import { getMatterIconClass, getMatterLabel } from '../../../constants/utilityFunctions';
import {
  cardBorderColorMapping,
  GENERAL_ENTITY_DISABLED_ID,
  tagColorMapping,
} from '../../../constants/tasks';
import { useNavigationStack } from '../../../context/NavigationStackContext';

const CriticalCardMail = ({
  criticalTask,
  completeTask,
}: {
  criticalTask: ITask;
  completeTask?: (task?: ITask) => void;
}) => {
  const { t } = useTranslation();
  const { push } = useNavigationStack();
  const { getAppLanguageCode } = useLanguage();
  const { updateTask, clearInitialTask } = useTask();
  const translatedStatus =
    criticalTask?.status?.translations?.find((t) => t.language === getAppLanguageCode())
      ?.translation ?? '-';
  const handleEditCriticalTask = (e: React.MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation();
    if (criticalTask?.id) {
      updateTask(null);
      clearInitialTask();
      push(`${ROUTES.ADD_EDIT_CRITICAL_TASK}/${criticalTask?.id}`);
    }
  };
  const handleGoToDetail = () => {
    push(ROUTES.CRITICAL_TASKS_DETAIL + '/' + criticalTask?.id);
  };
  return (
    <InteractiveCard
      data-testid="critical-card-mail"
      className={`primary-card task-card d-flex flex-col gap-15 h-full ${cardBorderColorMapping[criticalTask?.statusId] || cardBorderColorMapping.default}`}
      onActivate={handleGoToDetail}
    >
      <div className="d-flex justify-between w-full items-center">
        <div
          data-testid="status-chip"
          className={`status-chip d-flex justify-center items-center ${tagColorMapping[criticalTask?.statusId] || tagColorMapping.default}`}
        >
          {translatedStatus}
        </div>
        <Flex gap={10}>
          {criticalTask?.statusId !== GENERAL_ENTITY_DISABLED_ID && completeTask && (
            <Tooltip title={t('task:markAsComplete')} placement="bottom" arrow={false}>
              <IconButton
                aria-label={t('task:markAsComplete')}
                data-testid="mark-as-complete-button"
                icon={<CircleTick />}
                onClick={(e) => {
                  e?.stopPropagation();
                  completeTask?.(criticalTask);
                }}
              />
            </Tooltip>
          )}
          <Tooltip title={t('task:edit')} placement="bottom" arrow={false}>
            <IconButton
              aria-label={t('task:edit')}
              data-testid="edit-button"
              icon={<EditLinear />}
              onClick={handleEditCriticalTask}
            />
          </Tooltip>
          <Tooltip title={t('transaction:goDetail')} placement="bottom" arrow={false}>
            <IconButton
              aria-label={t('transaction:goDetail')}
              data-testid="go-detail-button"
              icon={<ArrowRight />}
              onClick={handleGoToDetail}
            />
          </Tooltip>
        </Flex>
      </div>
      <Divider />
      <div className="d-flex flex-col gap-15 flex-grow w-full">
        <CardData
          type="subject"
          icon={<Danger className="text-clr-primary-error" />}
          title={t('task:criticalTask.criticalTask')}
          content={criticalTask?.subject ?? '-'}
          contentclass="lineclamp-1"
        />
        <CardData
          type="matter"
          icon={
            <Briefcase
              className={getMatterIconClass(criticalTask?.matter?.invoiceableType ?? '')}
            />
          }
          title={getMatterLabel(t, criticalTask?.matter?.invoiceableType ?? '')}
          content={
            criticalTask?.matterId
              ? (criticalTask?.matter?.principalCompany?.name ??
                criticalTask?.matter?.principalPrivatePerson?.fullName ??
                '-')
              : '-'
          }
          subcontent={criticalTask?.matter?.matterNumber + ' - ' + criticalTask?.matter?.subject}
          contentclass="lineclamp-1"
          subcontentclass="lineclamp-1"
        />
      </div>
    </InteractiveCard>
  );
};

export default CriticalCardMail;
